/**
 * useContactForm — Feb 2026 refactor.
 *
 * Owns ALL state and handlers for the Add/Edit Contact dialog so that
 * ContactsPage doesn't have to. Pure extraction — zero behavior change
 * vs. the prior inline implementation. Every flow that worked before:
 *   - Multi-group add/save/reopen (Caleb fix — openEditDialog populates
 *     group_ids from the contact, not just group_id).
 *   - Family-collision preflight + GroupCollisionDialog parking the save.
 *   - Business card upload + AI extract.
 *   - Tags, spec_categories, vendor fields.
 *
 * Returns a single bag of state + setters + handlers; ContactsPage
 * passes the bag through to <ContactDialog> as props.
 *
 * @param {object} opts
 *   API:             string — REACT_APP_BACKEND_URL/api root
 *   fetchContacts:   () => void   refresh the list after save
 *   fetchTags:       () => void   refresh tag suggestions
 *   fetchGroups:     () => void   refresh group lookup map
 */
import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { categoryForKind, kindFromCategory } from '@/utils/partyKind';

function getEmptyForm() {
  return {
    first_name: '', last_name: '', company: '', job_title: '',
    group_id: null,
    group_ids: [],  // Feb 2026 multi-group
    category: 'Other', trade: '', trades: [], spec_categories: [], tags: [],
    // DP273-P2a — the Kind is what this contact IS to the firm; `category`
    // stays as the legacy label.
    party_kind: 'other',
    email: '', phone: '', phone_mobile: '', phone_work: '',
    address_street: '', address_city: '', address_state: '', address_zip: '',
    license_number: '', insurance_info: '', website: '', notes: '',
    business_card_image: '',
    // Vendor fields
    is_vendor: false, vendor_account_number: '', payment_terms: '',
    preferred_shipping: '', minimum_order: '', lead_times: null,
    return_policy: '', rep_name: '', rep_email: '', rep_phone: '',
    is_favorite_vendor: false, vendor_rating: null,
    // Iter 289 — tri-tier ownership. Default "tenant" (firm-wide directory).
    // Iter 291 — tier decoupled from sync. Share_mode_override drives sync
    // fan-out; null means "use category default".
    ownership_tier: 'tenant',
    share_mode_override: null,
  };
}

export default function useContactForm({ API, fetchContacts, fetchTags, fetchGroups }) {
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [newTag, setNewTag] = useState('');
  const [uploadingCard, setUploadingCard] = useState(false);
  const [extractingCard, setExtractingCard] = useState(false);
  const fileInputRef = useRef(null);
  // DP273-P2c — what the caller asked for when it opened the form: where to
  // report the new contact, and which project is in view. A ref so the save
  // reads the current value; state so the dialog can draw the extra button.
  const optionsRef = useRef(null);
  const [dialogOptions, setDialogOptions] = useState(null);

  // Same-name family disambiguation. Parked save + collision data.
  const [collisionDialog, setCollisionDialog] = useState({ open: false, data: null });
  const [pendingSubmit, setPendingSubmit] = useState(null);

  const resetForm = () => { setFormData(getEmptyForm()); setNewTag(''); };

  const openAddDialog = (initial, options) => {
    // DP273-P2c — the same form, opened from a memo or an email with what
    // that note already said. No argument at all behaves exactly as before.
    // A click event is not a seed: a button wired as onClick={openAddDialog}
    // hands its event here, and spreading it poisons the save.
    const isEvent = !!initial && typeof initial === 'object' && ('nativeEvent' in initial || typeof initial.preventDefault === 'function');
    const seed = initial && !isEvent ? initial : null;
    const seeded = seed ? { ...getEmptyForm(), ...seed } : getEmptyForm();
    // An intake that names a Kind gets the matching legacy label too, so the
    // record does not read "Other" while its kind says Subcontractor.
    if (seed?.party_kind && !seed?.category) {
      seeded.category = categoryForKind(seed.party_kind, null);
    }
    setFormData(seeded);
    setNewTag('');
    optionsRef.current = options || null;
    setDialogOptions(options || null);
    setEditingContact(null);
    setShowAddContact(true);
  };

  const openEditDialog = (contact) => {
    // Handle legacy contacts that have 'name' but not first_name/last_name
    let firstName = contact.first_name || '';
    let lastName = contact.last_name || '';
    if (!firstName && !lastName && contact.name) {
      const parts = contact.name.split(' ');
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ') || '';
    }
    setFormData({
      first_name: firstName,
      last_name: lastName,
      company: contact.company || '',
      group_id: contact.group_id || null,
      // Feb 2026 multi-group — preserve ALL existing memberships when the
      // user opens the edit dialog. Without this, adding a 2nd group via
      // MultiGroupPicker would overwrite (via the [group_id] fallback)
      // and silently drop any pre-existing additional memberships.
      group_ids: Array.isArray(contact.group_ids) && contact.group_ids.length > 0
        ? [...contact.group_ids]
        : (contact.group_id ? [contact.group_id] : []),
      job_title: contact.job_title || '',
      category: contact.category || 'Other',
      // DP273-P2a — a contact saved before the Kind existed opens on the kind
      // its old category word reads as.
      party_kind: contact.party_kind || kindFromCategory(contact.category, contact),
      trade: contact.trade || '',
      trades: contact.trades || [],
      trade_unmatched: contact.trade_unmatched || '',
      spec_categories: contact.spec_categories || [],
      tags: contact.tags || [],
      email: contact.email || '',
      phone: contact.phone || '',
      phone_mobile: contact.phone_mobile || '',
      phone_work: contact.phone_work || '',
      address_street: contact.address_street || '',
      address_city: contact.address_city || '',
      address_state: contact.address_state || '',
      address_zip: contact.address_zip || '',
      license_number: contact.license_number || '',
      insurance_info: contact.insurance_info || '',
      website: contact.website || '',
      notes: contact.notes || '',
      business_card_image: contact.business_card_image || '',
      is_vendor: contact.is_vendor || false,
      vendor_account_number: contact.vendor_account_number || '',
      payment_terms: contact.payment_terms || '',
      preferred_shipping: contact.preferred_shipping || '',
      minimum_order: contact.minimum_order || '',
      lead_times: contact.lead_times || null,
      return_policy: contact.return_policy || '',
      rep_name: contact.rep_name || '',
      rep_email: contact.rep_email || '',
      rep_phone: contact.rep_phone || '',
      is_favorite_vendor: contact.is_favorite_vendor || false,
      vendor_rating: contact.vendor_rating || null,
      // Preserve the contact's tier when editing so the checkbox reflects
      // reality (and doesn't downgrade a personal contact back to tenant
      // on the next save).
      ownership_tier: contact.ownership_tier || 'tenant',
      // Iter 291 — per-contact share override (null = use category default).
      share_mode_override: contact.share_mode_override || null,
    });
    setEditingContact(contact);
    setShowAddContact(true);
  };

  // Business card upload + AI extract.
  const handleBusinessCardUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }

    setUploadingCard(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = uploadRes.data.url;
      setFormData(prev => ({ ...prev, business_card_image: imageUrl }));

      setExtractingCard(true);
      try {
        const extractRes = await axios.post(`${API}/contacts/extract-from-card`, { image_url: imageUrl });
        if (extractRes.data) {
          const ex = extractRes.data;
          setFormData(prev => ({
            ...prev,
            first_name: ex.first_name || prev.first_name,
            last_name: ex.last_name || prev.last_name,
            company: ex.company || prev.company,
            job_title: ex.job_title || prev.job_title,
            email: ex.email || prev.email,
            phone: ex.phone || prev.phone,
            phone_mobile: ex.phone_mobile || prev.phone_mobile,
            website: ex.website || prev.website,
            address_street: ex.address_street || prev.address_street,
            address_city: ex.address_city || prev.address_city,
            address_state: ex.address_state || prev.address_state,
            address_zip: ex.address_zip || prev.address_zip,
          }));
          toast.success('Contact info extracted from business card!');
        }
      } catch { toast.info('Image uploaded. Manual entry required.'); }
    } catch { toast.error('Failed to upload image'); }
    finally { setUploadingCard(false); setExtractingCard(false); }
  };

  // Returns true if the save should PROCEED; false if the collision
  // dialog opened and will drive the save via executePendingSubmit.
  const preflightFamilyCollision = async (payload) => {
    const hasGroup = !!payload.group_id;
    const hasCompany = !!(payload.company || '').trim();
    const hasLastName = !!(payload.last_name || '').trim();
    if (hasGroup || hasCompany || !hasLastName) return true;
    try {
      const params = new URLSearchParams({
        type: 'family',
        name: payload.last_name.trim(),
      });
      if (payload.first_name) params.set('first_name', payload.first_name);
      if (payload.address_city) params.set('city', payload.address_city);
      // When editing an already-grouped contact, exclude its own group
      // from the candidate list so the prompt doesn't show "is this the
      // same Smith you're already in?"
      if (editingContact?.group_id) params.set('exclude_id', editingContact.group_id);
      const res = await axios.get(`${API}/contact-groups/check-collision?${params.toString()}`);
      if (res.data?.blocking) {
        setPendingSubmit({ payload, isEdit: !!editingContact, editingId: editingContact?.id });
        setCollisionDialog({ open: true, data: res.data });
        return false;
      }
    } catch (err) {
      // Best-effort preflight — never block save on network error.
      console.warn('Collision preflight skipped:', err?.message);
    }
    return true;
  };

  const executePendingSubmit = async (resolvedGroupId) => {
    if (!pendingSubmit) {
      setCollisionDialog({ open: false, data: null });
      return;
    }
    const { payload, isEdit, editingId } = pendingSubmit;
    const final = { ...payload, group_id: resolvedGroupId };
    try {
      if (isEdit && editingId) {
        await axios.put(`${API}/contacts/${editingId}`, final);
        toast.success('Contact updated');
      } else {
        await axios.post(`${API}/contacts`, final);
        toast.success('Contact created');
      }
      setShowAddContact(false); resetForm(); fetchContacts(); fetchTags(); fetchGroups();
    } catch {
      toast.error('Failed to save contact');
    } finally {
      setPendingSubmit(null);
      setCollisionDialog({ open: false, data: null });
    }
  };

  const handleSubmit = async (addToTeam) => {
    // DP273-P2c — the dialog's own button passes a click event; only a
    // literal true means "and add them to the project team".
    const alsoAddToTeam = addToTeam === true;
    if (!formData.first_name || !formData.last_name) { toast.error('First and last name are required'); return; }
    const payload = { ...formData };
    delete payload.trade_unmatched;   // read-only: the server derives it
    if (payload.minimum_order === '') payload.minimum_order = null;
    const proceed = await preflightFamilyCollision(payload);
    if (!proceed) return;  // collision dialog took over
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, payload);
        toast.success('Contact updated');
      } else {
        const res = await axios.post(`${API}/contacts`, payload);
        // POST /contacts answers with the saved row itself; a wrapped
        // { contact: … } shape is read too, in case that ever changes.
        const created = res.data?.contact || res.data;
        const options = optionsRef.current;
        if (options?.onSaved) {
          try { options.onSaved(created); } catch { /* the save still stands */ }
        }
        const project = options?.project;
        if (alsoAddToTeam && project?.id && created?.id) {
          const trade = formData.trades?.[0] || formData.trade || null;
          try {
            await axios.post(`${API}/projects/${project.id}/memberships`, {
              contact_id: created.id,
              party_kind: formData.party_kind,
              trade,
              role: trade,
            });
            toast.success(`Saved and added to ${project.name || 'the project'}`);
          } catch {
            toast.error(`Saved, but could not add them to ${project.name || 'the project'}`);
          }
        } else {
          toast.success('Contact created');
        }
      }
      optionsRef.current = null;
      setDialogOptions(null);
      setShowAddContact(false); resetForm(); fetchContacts(); fetchTags(); fetchGroups();
    } catch (err) { toast.error(err?.response?.data?.detail ? `Could not save: ${err.response.data.detail}` : 'Failed to save contact'); }
  };

  const addTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({ ...formData, tags: [...formData.tags, newTag] });
      setNewTag('');
    }
  };
  const removeTag = (tag) => setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });

  return {
    // State
    showAddContact, setShowAddContact,
    editingContact, setEditingContact,
    formData, setFormData,
    newTag, setNewTag,
    uploadingCard, extractingCard,
    fileInputRef,
    collisionDialog, setCollisionDialog,
    pendingSubmit, setPendingSubmit,
    // DP273-P2c — the intake's own request (project in view, onSaved).
    options: dialogOptions,
    // Handlers
    resetForm,
    openAddDialog, openEditDialog,
    handleBusinessCardUpload,
    preflightFamilyCollision, executePendingSubmit, handleSubmit,
    addTag, removeTag,
  };
}
